import React, { useEffect } from 'react';
import type { FormField } from '../interfaces';
import {
    evaluateExpression,
    expressionContainsEmptyOperand,
    findCamundaProperty,
    findCamundaPropertyValue,
    getLocalizedLabel,
} from '../utils';
import { useFormikContext } from 'formik';
import { FormikNumericFormat } from './FormikNumericFormat';
import type { FormValuesType } from './FormContainer';

export const CalculatedSelector = ({
    field,
    isReadOnly,
    isRequired,
}: {
    field: FormField;
    isReadOnly?: boolean;
    isRequired?: boolean;
}) => {
    const { setFieldValue, values } = useFormikContext<FormValuesType>();

    const calculatePropertyValue = findCamundaPropertyValue(
        'calculate',
        field.properties,
    );
    const hiddenIfEmptyOperandPropertyExists = findCamundaProperty(
        'hiddenIfEmptyOperand',
        field.properties,
    );

    useEffect(() => {
        if (!calculatePropertyValue) return;

        const result = evaluateExpression<number>(
            calculatePropertyValue,
            values,
        );
        if (result === undefined) return;

        const resultRound = +result.toFixed(2);

        setFieldValue(field.id as string, resultRound.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    const hiddenFieldFromEmptyOperand =
        hiddenIfEmptyOperandPropertyExists &&
        expressionContainsEmptyOperand(values, calculatePropertyValue);

    if (hiddenFieldFromEmptyOperand) return null;

    return (
        <FormikNumericFormat
            variant="outlined"
            id={field.id}
            placeholder={
                findCamundaPropertyValue('placeholder', field.properties) || ''
            }
            defaultValue={field.id ?? field.defaultValue}
            isRequired={isRequired}
            isReadOnly={isReadOnly}
            label={getLocalizedLabel(
                `project.formFields.${field.id}`,
                field.label,
                field.id,
            )}
        />
    );
};
